import { Cover } from '../primitives';
import { localizedField } from '../../locales/content';
import { hapticImpact } from '../../utils/haptics';
import TitleImageOverlay from './TitleImageOverlay';

/**
 * PresetGroupCard — одиночная карточка preset-группы на Home (single-cover).
 * Figma: cover instances 123:2921 / 123:2913 / 123:2910 / 123:2909
 * (variants viral / packs / lux / hot) — все идентичны shell-ом, отличаются
 * только image-ref-ом.
 *
 * Composition: this component is a thin wrapper around <Cover> primitive
 * (componentId 123:2927), which gives us:
 *   - 160×160, rounded-home-24, overflow-hidden (Figma geometry)
 *   - NewBadge top-right via Figma constraints {vertical:TOP, horizontal:RIGHT}
 *   - NO invented bottom-gradient overlay (strict-audit §8 confirmed none)
 *   - NO invented text-fallback (strict-audit §8 confirmed not in Figma)
 *
 * On top of Cover we add:
 *   - Interactive <button> wrapper (tap handler)
 *   - Optional absolute <img> titleImage overlay (real PNG from admin data,
 *     positioned bottom-left per Figma for the packs/viral title cards).
 *
 * Strict-audit §8 closures (2 MAJOR + 1 CRITICAL + 1 INVENTED):
 *   1. rounded-home-16 → rounded-home-24 (via Cover primitive).
 *   2. Removed invented bottom-gradient rgba(1,1,3,0)→(0.5) — not in Figma.
 *   3. Removed invented text-fallback `<span>{name}</span>` uppercase —
 *      Figma has only image, no text overlay.
 *   4. NewBadge position top-2 left-2 → top-0 right-0 (via Cover primitive,
 *      Figma constraints TOP/RIGHT).
 *
 * Props:
 *   group    — { id, name, cover, titleImage?, isNew? }
 *   onClick  — (group) => void; tap handler
 */
export default function PresetGroupCard({ group, onClick, fluid = false }) {
  if (!group) return null;

  const { cover, coverThumb, titleImage, isNew } = group;
  const name = localizedField(group, 'name') || group.name;

  return (
    <button
      type="button"
      onClick={() => {
        hapticImpact('light');
        if (onClick) onClick(group);
      }}
      className={[
        'relative flex-shrink-0',
        fluid ? 'aspect-square w-full' : '',
        'text-left',
        // Reset default button styling so Cover is the visual root.
        'p-0 border-0 bg-transparent',
      ].join(' ')}
      aria-label={name}
    >
      <Cover
        coverUrl={coverThumb || cover}
        alt={name || ''}
        isNew={isNew}
        style={fluid ? { width: '100%', height: '100%' } : undefined}
      />

      {/* Optional title PNG overlay — present only when admin supplied
          titleImage. Figma position for title image (when present):
          relative to cover (12, bottom-12). */}
      <TitleImageOverlay src={titleImage} group={group} />
    </button>
  );
}
